import React, { useRef, useEffect } from 'react';
import { Brain, MessageSquare, Send, Upload, X, Image, Video } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import { ALLOWED_IMAGE_MIME_TYPES } from '../../simulationUseCase/useCaseForms/conceptImageFormats';

// Sim-chat accepts PNG/JPG images (the vision backend's supported set, matching the
// concept/packaging uploads) plus video. Derived from the shared image contract so it can't
// drift back to a bare image/* that offers SVG/GIF the backend rejects.
const CHAT_UPLOAD_ACCEPT = [...ALLOWED_IMAGE_MIME_TYPES, '.jpg', '.jpeg', '.png', 'video/*'].join(
  ',',
);

interface ChatMessage {
  id?: string;
  role: string;
  content: string;
  timestamp?: string;
  media?: {
    images?: string[];
    videos?: string[];
  };
}

interface ChatState {
  messages: ChatMessage[];
  isTyping: boolean;
  isSending: boolean;
  error: string | null;
}

interface ChatInterfaceProps {
  chatTab: 'simulation' | 'persona';
  chatState: ChatState;
  chatMessage: string;
  mediaFiles: File[];
  mediaPreviewUrls: string[];
  isChatConnected: boolean;
  isUploadingMedia: boolean;
  isPreparingExport: boolean;
  onChatTabChange: (tab: 'simulation' | 'persona') => void;
  onChatMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMedia: (index: number) => void;
  onUploadClick: () => void;
  onViewMedia: (type: 'image' | 'video', src: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  /** Optional query budget (marketplace chat-with-simulation). When set, a
   *  counter is shown and sending is blocked once the cap is reached. */
  queryCap?: number;
  queriesUsed?: number;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  chatTab,
  chatState,
  chatMessage,
  mediaFiles,
  mediaPreviewUrls,
  isChatConnected,
  isUploadingMedia,
  isPreparingExport,
  onChatTabChange,
  onChatMessageChange,
  onSendMessage,
  onKeyDown,
  onFileSelect,
  onRemoveMedia,
  onUploadClick,
  onViewMedia,
  fileInputRef,
  queryCap,
  queriesUsed,
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const capReached = queryCap != null && (queriesUsed ?? 0) >= queryCap;
  const [showUploadTooltip, setShowUploadTooltip] = React.useState(false);

  // Auto-scroll chat to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatState.messages]);

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 bg-white">
        <nav className="flex items-center justify-between gap-4 px-6" aria-label="Tabs">
          <button
            onClick={() => onChatTabChange('simulation')}
            className={`py-4 px-1 inline-flex items-center border-b-2 text-sm font-medium ${
              chatTab === 'simulation'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Brain className="mr-2 h-4 w-4" />
            Chat with Simulation
          </button>
          {/* Query allowance, shown up front rather than only as fine print under
              the input — a capped chat is worth knowing about BEFORE you type. */}
          {queryCap != null && (
            <span
              className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${
                capReached ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'
              }`}
              title={`${queriesUsed ?? 0} of ${queryCap} queries used`}
            >
              {capReached
                ? `0 of ${queryCap} queries left`
                : `${Math.max(0, queryCap - (queriesUsed ?? 0))} of ${queryCap} queries left`}
            </span>
          )}
        </nav>
      </div>

      <div className="flex-1 overflow-auto p-4 bg-gray-50" ref={chatContainerRef}>
        <div className="space-y-4">
          {/* Media upload message */}
          {isUploadingMedia && (
            <div className="flex justify-center mb-4">
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg shadow-sm max-w-[90%] animate-fadeIn">
                <div className="flex items-center gap-3">
                  <svg
                    className="animate-spin h-5 w-5 text-blue-600 flex-shrink-0"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <div className="text-sm">
                    <div className="font-semibold mb-1">Uploading Media</div>
                    <div>Converting and sending your images and videos to the simulation...</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {chatState.messages.length === 0 ? (
            <div className="text-center py-12 px-6">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8FCFA]">
                <MessageSquare className="h-6 w-6 text-[#0B7C74]" />
              </div>
              <p className="text-[15px] font-medium text-[#111]">Ask the simulation anything</p>
              <p className="mt-1 text-[13px] text-[#6b7280]">
                Dig into the results — why a segment reacted the way it did, what would change their
                mind, or how to sharpen the concept.
              </p>
              {!isChatConnected && (
                <div className="mt-4 flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-2 rounded-lg">
                  <svg className="animate-spin h-4 w-4 text-teal-600" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  <span className="text-sm font-medium">Loading profiles, please wait...</span>
                </div>
              )}
            </div>
          ) : (
            chatState.messages.map((msg, idx) => {
              const isUserMessage =
                chatTab === 'simulation' ? msg.role === 'use' : msg.role === 'use_persona';
              const mediaData = isUserMessage ? msg.media : null;
              const isLastUserMessage = isUserMessage && idx === chatState.messages.length - 1;

              return (
                <div
                  key={msg.id || idx}
                  className={`flex flex-col ${
                    isUserMessage ? 'items-end' : 'items-start'
                  } animate-fadeIn`}
                  data-idx={idx}
                >
                  <div className={`flex ${isUserMessage ? 'justify-end' : 'justify-start'} w-full`}>
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-lg ${
                        isUserMessage
                          ? 'bg-blue-500 text-white rounded-br-none animate-slideInRight'
                          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none animate-slideInLeft'
                      }`}
                    >
                      {/* Display attached media if present */}
                      {mediaData &&
                        ((mediaData.images?.length ?? 0) > 0 ||
                          (mediaData.videos?.length ?? 0) > 0) && (
                          <div className="mb-2 flex flex-wrap gap-2">
                            {/* Images */}
                            {mediaData.images?.map((img, imgIdx) => (
                              <div key={`img-${imgIdx}`} className="relative cursor-pointer group">
                                <img
                                  src={img}
                                  alt={`Attached ${imgIdx + 1}`}
                                  className="max-w-[200px] max-h-[150px] rounded border border-white/20 object-cover"
                                  onClick={() => onViewMedia('image', img)}
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded flex items-center justify-center pointer-events-none">
                                  <svg
                                    className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </div>
                              </div>
                            ))}

                            {/* Videos */}
                            {mediaData.videos?.map((vid, vidIdx) => (
                              <div
                                key={`vid-${vidIdx}`}
                                className="relative cursor-pointer group w-[200px] h-[150px]"
                              >
                                <video
                                  src={vid}
                                  className="w-full h-full rounded border border-white/20 object-cover bg-gray-800"
                                  controls={false}
                                  muted
                                  playsInline
                                  preload="metadata"
                                  onLoadedMetadata={(e) => {
                                    const video = e.currentTarget;
                                    video.currentTime = 0.1;
                                  }}
                                />
                                <div
                                  className="absolute inset-0 bg-black bg-opacity-40 rounded flex items-center justify-center hover:bg-opacity-50 transition-opacity"
                                  onClick={() => onViewMedia('video', vid)}
                                >
                                  <div className="bg-white/90 rounded-full p-3 hover:scale-110 transition-transform">
                                    <svg
                                      className="h-6 w-6 text-gray-800"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                      <MarkdownRenderer
                        content={msg.content}
                        variant="chat"
                        className={isUserMessage ? 'text-white' : ''}
                        isExporting={isPreparingExport}
                      />
                    </div>
                  </div>
                  {/* Show sending indicator below the last user message */}
                  {isLastUserMessage && chatState.isSending && !chatState.isTyping && (
                    <div className="flex items-center gap-1.5 mt-1 mr-2 text-xs text-gray-500">
                      <svg
                        className="animate-pulse h-3 w-3"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M22 2L11 13" />
                        <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                      </svg>
                      <span>Sending...</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
          {chatState.isTyping && (
            <div className="flex justify-start animate-pulse">
              <div className="bg-white border border-gray-200 text-gray-500 px-4 py-2 rounded-lg rounded-bl-none max-w-[80%]">
                <div className="flex space-x-2">
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-0"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                  <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce delay-500"></div>
                </div>
              </div>
            </div>
          )}
          {chatState.error && (
            <div className="flex justify-start">
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg rounded-bl-none max-w-[80%]">
                <div className="flex items-center space-x-2">
                  <span className="text-sm">Error: {chatState.error}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100 bg-white">
        {/* Media preview area */}
        {mediaFiles.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-600 font-medium">
                Attached Media ({mediaFiles.length}/4)
              </span>
              <button
                onClick={() => {
                  mediaPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
                  onRemoveMedia(-1); // Signal to clear all
                }}
                className="text-xs text-red-500 hover:text-red-700"
                type="button"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {mediaFiles.map((file, index) => (
                <div key={index} className="relative group">
                  {file.type.startsWith('image/') ? (
                    <div className="relative">
                      <img
                        src={mediaPreviewUrls[index]}
                        alt={file.name}
                        className="h-16 w-16 object-cover rounded border border-gray-200"
                      />
                      <Image className="absolute bottom-1 right-1 h-3 w-3 text-white bg-blue-500 rounded p-0.5" />
                    </div>
                  ) : (
                    <div className="relative">
                      <video
                        src={mediaPreviewUrls[index]}
                        className="h-16 w-16 object-cover rounded border border-gray-200 bg-gray-100"
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                          const video = e.currentTarget;
                          video.currentTime = 0.1;
                        }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-50 rounded-full p-1">
                          <Video className="h-4 w-4 text-white" />
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => onRemoveMedia(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    type="button"
                  >
                    <X className="h-3 w-3" />
                  </button>
                  <div
                    className="text-xs text-gray-500 mt-1 truncate max-w-[64px]"
                    title={file.name}
                  >
                    {file.name}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input controls */}
        <div className="p-4">
          <div className="flex items-end gap-2 relative">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              // Images restricted to PNG/JPG (the vision backend's supported formats, matching
              // the concept/packaging uploads); video still allowed. A bare image/* offered SVG
              // and other formats the picker would accept but the backend then rejected.
              accept={CHAT_UPLOAD_ACCEPT}
              onChange={onFileSelect}
              className="hidden"
            />

            {/* Upload button with custom tooltip */}
            <div className="relative">
              <button
                onClick={onUploadClick}
                onMouseEnter={() => setShowUploadTooltip(true)}
                onMouseLeave={() => setShowUploadTooltip(false)}
                className={`p-2 rounded-lg border transition-colors flex items-center gap-1 ${
                  mediaFiles.length >= 4
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                    : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
                type="button"
                disabled={
                  chatState.isTyping ||
                  chatState.isSending ||
                  isUploadingMedia ||
                  mediaFiles.length >= 4
                }
              >
                {isUploadingMedia ? (
                  <svg className="animate-spin h-5 w-5 text-blue-500" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                ) : (
                  <Upload
                    className={`h-5 w-5 ${mediaFiles.length >= 4 ? 'text-gray-400' : 'text-gray-600'}`}
                  />
                )}
                {mediaFiles.length > 0 && !isUploadingMedia && (
                  <span
                    className={`text-xs font-medium ${mediaFiles.length >= 4 ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    {mediaFiles.length}/4
                  </span>
                )}
              </button>

              {/* Custom visible tooltip */}
              {showUploadTooltip && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-[15] bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg pointer-events-none">
                  {isUploadingMedia
                    ? 'Uploading...'
                    : mediaFiles.length >= 4
                      ? 'Max 4 files'
                      : 'Upload images/videos (Max 20MB)'}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>

            <textarea
              ref={textareaRef}
              value={chatMessage}
              onChange={(e) => onChatMessageChange(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask here"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500 placeholder:text-sm placeholder:text-gray-400"
              rows={2}
              disabled={chatState.isTyping || chatState.isSending || isUploadingMedia || capReached}
            />
            <button
              onClick={onSendMessage}
              disabled={
                chatState.isTyping ||
                chatState.isSending ||
                isUploadingMedia ||
                capReached ||
                (!chatMessage.trim() && mediaFiles.length === 0)
              }
              aria-label="Send message"
              title={
                isUploadingMedia
                  ? 'Uploading media...'
                  : chatState.isSending
                    ? 'Sending message...'
                    : chatState.isTyping
                      ? 'AI is responding...'
                      : !chatMessage.trim() && mediaFiles.length === 0
                        ? 'Type a message or attach media'
                        : mediaFiles.length > 0
                          ? `Send message with ${mediaFiles.length} file(s)`
                          : 'Send message'
              }
              className={`flex items-center justify-center p-3 rounded-full min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 ${
                (chatMessage.trim() || mediaFiles.length > 0) &&
                !chatState.isTyping &&
                !chatState.isSending &&
                !isUploadingMedia
                  ? 'bg-[#0B7C74] text-white hover:bg-[#028B7E] shadow-sm'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between text-xs text-gray-400">
            {queryCap != null ? (
              <span className={capReached ? 'text-amber-600 font-medium' : ''}>
                {capReached
                  ? `You've used all ${queryCap} chat queries for this study.`
                  : `${Math.max(0, queryCap - (queriesUsed ?? 0))} of ${queryCap} queries left`}
              </span>
            ) : (
              <span />
            )}
            <span>Press Enter to send, Shift+Enter for new line</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
